import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { billingApi } from '../lib/api';
import { toast } from 'sonner';
import {
  Sparkle, Clock, Star, Buildings, ArrowRight, X, WarningCircle, Crown,
} from '@phosphor-icons/react';

// Same accent colors as the pricing page
const PLAN_META = {
  starter:      { label: 'Starter',      accent: '#41B6E6', icon: Sparkle },
  professional: { label: 'Professional', accent: '#002677', icon: Star },
  business:     { label: 'Business',     accent: '#8347AD', icon: Buildings },
  enterprise:   { label: 'Enterprise',   accent: '#E4002B', icon: Crown },
};

export default function SubscriptionBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('subbanner_dismissed') === '1'; } catch { return false; }
  });
  const [extending, setExtending] = useState(false);

  const plan = user?.plan;
  const billing = user?.billing_cycle || 'monthly';
  const endsAt = user?.plan_period_ends_at;

  const { daysLeft, state } = useMemo(() => {
    if (!endsAt) return { daysLeft: null, state: plan ? 'active' : 'free' };
    try {
      const end = new Date(endsAt).getTime();
      const now = Date.now();
      const ms = end - now;
      const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
      if (days <= 0) return { daysLeft: days, state: 'expired' };
      if (days <= 7) return { daysLeft: days, state: 'expiring' };
      return { daysLeft: days, state: 'active' };
    } catch {
      return { daysLeft: null, state: 'active' };
    }
  }, [endsAt, plan]);

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem('subbanner_dismissed', '1'); } catch { /* noop */ }
  };

  const handleExtend = async () => {
    if (!plan) return;
    setExtending(true);
    try {
      const packageId = `${plan}_${billing}`;
      const { data } = await billingApi.checkoutSession(packageId, window.location.origin, user?.email);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Unable to open checkout. Please try again.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to open checkout.');
    } finally {
      setExtending(false);
    }
  };

  if (!user || dismissed) return null;

  // --- Free tier banner ---
  if (state === 'free') {
    return (
      <BannerShell
        onDismiss={handleDismiss}
        tint="#002677"
        testId="subscription-banner-free"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="h-9 w-9 rounded-sm bg-white/15 flex items-center justify-center flex-shrink-0">
            <Sparkle size={16} weight="fill" className="text-white" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-sm text-white truncate">
              You&rsquo;re on the Always Open Free plan.
            </p>
            <p className="text-[12px] text-white/80 truncate">
              Unlock unlimited data rooms, AI copilot and automations with a 30-day paid access.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to="/pricing"
            className="rounded-sm h-9 px-4 inline-flex items-center gap-2 bg-white text-[#002677] text-sm font-semibold hover:bg-white/90 transition-colors"
            data-testid="subscription-cta-upgrade"
          >
            See plans <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </BannerShell>
    );
  }

  const meta = PLAN_META[plan] || PLAN_META.professional;
  const Icon = meta.icon;

  // --- Expired ---
  if (state === 'expired') {
    return (
      <BannerShell
        onDismiss={handleDismiss}
        tint="#E4002B"
        testId="subscription-banner-expired"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="h-9 w-9 rounded-sm bg-white/15 flex items-center justify-center flex-shrink-0">
            <WarningCircle size={18} weight="fill" className="text-white" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-sm text-white truncate">
              Your {meta.label} access has expired.
            </p>
            <p className="text-[12px] text-white/85 truncate">
              Extend another 30 days to restore full access to your data rooms and copilot.
            </p>
          </div>
        </div>
        <button
          onClick={handleExtend}
          disabled={extending}
          className="rounded-sm h-9 px-4 inline-flex items-center gap-2 bg-white text-[#E4002B] text-sm font-semibold hover:bg-white/90 transition-colors disabled:opacity-60"
          data-testid="subscription-cta-renew"
        >
          {extending ? 'Opening…' : 'Renew now'}
          {!extending && <ArrowRight size={14} weight="bold" />}
        </button>
      </BannerShell>
    );
  }

  // --- Active / Expiring ---
  const isUrgent = state === 'expiring';
  return (
    <BannerShell
      onDismiss={handleDismiss}
      tint={isUrgent ? '#B5940A' : meta.accent}
      testId={`subscription-banner-${state}`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="h-9 w-9 rounded-sm bg-white/15 flex items-center justify-center flex-shrink-0">
          <Icon size={16} weight={isUrgent ? 'fill' : 'duotone'} className="text-white" />
        </div>
        <div className="min-w-0 flex items-center gap-3 flex-wrap">
          <p className="text-sm text-white">
            <span className="font-semibold">{meta.label}</span>
            <span className="text-white/70"> · {billing === 'annual' ? 'Annual' : 'Monthly'}</span>
          </p>
          <span className="h-1 w-1 rounded-full bg-white/40" />
          <p className="text-sm text-white/90 flex items-center gap-1.5">
            <Clock size={13} weight={isUrgent ? 'fill' : 'regular'} />
            {daysLeft} day{daysLeft === 1 ? '' : 's'} left
            {isUrgent && (
              <span className="ml-1 text-[10px] tracking-[0.18em] uppercase font-bold px-1.5 py-0.5 rounded-full bg-white text-[#B5940A]">
                Renew soon
              </span>
            )}
          </p>
        </div>
      </div>
      <button
        onClick={handleExtend}
        disabled={extending}
        className="rounded-sm h-9 px-4 inline-flex items-center gap-2 bg-white text-sm font-semibold hover:bg-white/90 transition-colors disabled:opacity-60"
        style={{ color: isUrgent ? '#B5940A' : meta.accent }}
        data-testid="subscription-cta-extend"
      >
        {extending ? 'Opening…' : 'Extend another period'}
        {!extending && <ArrowRight size={14} weight="bold" />}
      </button>
    </BannerShell>
  );
}

function BannerShell({ children, onDismiss, tint, testId }) {
  return (
    <div
      className="rounded-sm px-4 py-3 flex items-center gap-3 shadow-sm mb-4"
      style={{ background: tint }}
      data-testid={testId}
    >
      {children}
      <button
        onClick={onDismiss}
        className="p-1.5 rounded-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
        aria-label="Dismiss banner"
        data-testid="subscription-banner-dismiss"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
